import { type ComponentType, useSyncExternalStore } from "react";

/** Downloads started with `warm()` that nobody has asked for yet. */
let quietInFlight = 0;

/**
 * Whether a download nobody asked for is still in flight. `main.tsx` reloads
 * the page when a chunk fails to load (a tab older than the deploy); a
 * warm-up failing in a dead zone must not do that on its own, least of all
 * to a scorekeeper, whose reload lands on the offline gate.
 */
export function quietLoadInFlight(): boolean {
  return quietInFlight > 0;
}

/**
 * A lazily loaded component that can be fetched BEFORE it is needed and is
 * rendered without `Suspense`.
 *
 * `React.lazy` inside a fresh `<Suspense>` looked free and was not: React 19
 * holds back revealing a boundary for 300 ms after it last showed a fallback
 * (`FALLBACK_THROTTLE_MS`, react-dom 19.2), so the first Menu tap after a page
 * load waited ~305 ms with the chunk already in hand (measured 2026-09-14:
 * chunks fetched in 3 ms, no long task, dialog in the DOM at 305 ms). The sheet
 * then arrived mid-gesture and read as a stutter.
 *
 * So the component lives in a module-level slot and there is no boundary to
 * throttle. Two ways to fetch it:
 *
 * - `load()` is a person asking (a finger down, a tap). It resolves true once
 *   the component is in hand, false if it could not be fetched; the caller
 *   opens its dialog on true, so a failed tap leaves nothing half-open.
 * - `warm()` is nobody asking yet (the page went idle). Skipped offline, and
 *   its failure is not a reason to reload (`quietLoadInFlight`). A `load()`
 *   while it is in flight turns it into an asked-for download.
 *
 * A failed download clears itself, so the next attempt tries again.
 */
export interface Preloadable<P> {
  /**
   * `quiet`: a failure is the caller's to explain, never a reason for
   * `main.tsx` to reload. The scorekeeper asks this way: its "Next game" tap
   * in a dead zone used to fail the chunk, reload the page and land the
   * volunteer on the offline gate with no score and no undo.
   */
  load: (opts?: { quiet?: boolean }) => Promise<boolean>;
  warm: () => void;
  /** The component once it is loaded, else null. */
  useComponent: () => ComponentType<P> | null;
}

export function preloadable<P>(fetch: () => Promise<ComponentType<P>>): Preloadable<P> {
  let component: ComponentType<P> | null = null;
  let pending: Promise<boolean> | null = null;
  let quiet = false;
  const listeners = new Set<() => void>();
  const subscribe = (listen: () => void) => {
    listeners.add(listen);
    return () => {
      listeners.delete(listen);
    };
  };
  const hush = (): void => {
    if (!quiet) return;
    quiet = false;
    quietInFlight -= 1;
  };
  const start = (): Promise<boolean> =>
    fetch().then(
      (loaded) => {
        hush();
        component = loaded;
        for (const listen of listeners) listen();
        return true;
      },
      () => {
        hush();
        pending = null;
        return false;
      },
    );
  return {
    load: (opts) => {
      if (component !== null) return Promise.resolve(true);
      if (opts?.quiet === true) {
        // Asked for, but the asker handles the failure: keep (or start) the
        // download quiet so a dead zone does not reload the page.
        if (pending === null) {
          quiet = true;
          quietInFlight += 1;
          pending = start();
        }
        return pending;
      }
      // Somebody wants it now: a failure is theirs to see, not a quiet one.
      hush();
      pending ??= start();
      return pending;
    },
    warm: () => {
      if (component !== null || pending !== null || navigator.onLine === false) return;
      quiet = true;
      quietInFlight += 1;
      pending = start();
    },
    useComponent: () =>
      useSyncExternalStore(
        subscribe,
        () => component,
        () => null,
      ),
  };
}

/**
 * Run `task` once the page has settled: after first paint and whatever the
 * route mounted, so a warm-up never competes with the content it serves.
 * Safari has no `requestIdleCallback`, hence the timer. Returns a cancel.
 */
export function whenIdle(task: () => void): () => void {
  if (typeof window.requestIdleCallback === "function") {
    const id = window.requestIdleCallback(task, { timeout: 4000 });
    return () => window.cancelIdleCallback(id);
  }
  const id = window.setTimeout(task, 1500);
  return () => window.clearTimeout(id);
}
